import type { File } from "schemas/generated/file";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import Popover from "@replo/design-system/components/popover/Popover";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChevronDown } from "lucide-react";

import PageSelectionDropdown from "./PageSelectionDropdown";

type EditablePageNameProps = {
  page: Pick<File, "id" | "name">;
  projectId: string;
  setIsRenaming: (isRenaming: boolean) => void;
};

type PageNameWithDropdownProps = {
  page: Pick<File, "id" | "name" | "pageRoute" | "createdAt" | "updatedAt">;
  projectId: string;
  isPageSelectionPopoverOpen: boolean;
  setIsPageSelectionPopoverOpen: (open: boolean) => void;
  onPageSelect: (page: Pick<File, "id" | "pageRoute">) => void;
};
export function EditablePageName({
  page,
  projectId,
  setIsRenaming,
}: EditablePageNameProps): React.ReactNode {
  const [editablePageName, setEditablePageName] = React.useState(page.name);

  const { mutate: updatePageName } = trpc.nextJSProject.updatePage.useMutation({
    onSuccess: () => {
      void trpcUtils.nextJSProject.getPage.invalidate();
      void trpcUtils.nextJSProject.listPages.invalidate();
    },
  });

  const handleSave = () => {
    if (editablePageName.trim() === "") {
      setEditablePageName(page.name);
      setIsRenaming(false);
      return;
    }

    if (editablePageName !== page.name) {
      updatePageName({
        projectId,
        id: page.id,
        updates: {
          newName: editablePageName,
        },
      });
    }
    setIsRenaming(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setEditablePageName(page.name);
      setIsRenaming(false);
    }
  };

  React.useEffect(() => {
    setEditablePageName(page.name);
  }, [page.name]);

  return (
    <Input
      size="base"
      type="text"
      value={editablePageName}
      onChange={(e) => {
        e.stopPropagation();
        setEditablePageName(e.target.value);
      }}
      onKeyDown={handleKeyDown}
      onBlur={handleSave}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      autoFocus
      unsafe_className="w-[220px]"
    />
  );
}

export default function EditablePageNameWithDropdown({
  page,
  projectId,
  isPageSelectionPopoverOpen,
  setIsPageSelectionPopoverOpen,
  onPageSelect,
}: PageNameWithDropdownProps) {
  const [isRenamingPage, setIsRenamingPage] = React.useState(false);

  return (
    <div className="flex items-center gap-2">
      {isRenamingPage ? (
        <EditablePageName
          page={page}
          projectId={projectId}
          setIsRenaming={setIsRenamingPage}
        />
      ) : (
        <div className="flex flex-row">
          <Button
            variant="tertiary"
            size="base"
            UNSAFE_className={twMerge(
              "rounded-tr-none rounded-br-none border-r-0 px-2 py-1 hover:bg-selectable-hover transition-colors max-w-[200px] hover:no-underline",
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsRenamingPage(true);
            }}
          >
            <Tooltip content={page.name} triggerAsChild>
              <span className="header-h4 overflow-hidden text-ellipsis whitespace-nowrap max-w-[180px]">
                {page.name}
              </span>
            </Tooltip>
          </Button>
          <Popover
            isOpen={isPageSelectionPopoverOpen}
            onOpenChange={setIsPageSelectionPopoverOpen}
            hideCloseButton
            align="end"
            side="bottom"
            collisionPadding={12}
            triggerAsChild
            content={
              <PageSelectionDropdown
                projectId={projectId}
                handlePageSelectionCardClick={onPageSelect}
                currentPageId={page.id}
              />
            }
            shouldPreventDefaultOnInteractOutside={false}
            className="w-[312px] max-h-[600px] overflow-y-auto"
          >
            <IconButton
              variant="tertiary"
              size="base"
              UNSAFE_className={twMerge(
                "rounded-tl-none rounded-bl-none border-l-0 hover:bg-selectable-hover transition-colors hover:no-underline",
                isPageSelectionPopoverOpen && "bg-selectable-hover",
              )}
              icon={<ChevronDown size={10} />}
            />
          </Popover>
        </div>
      )}
    </div>
  );
}
